/**
 * ORB 封顶模拟 — what-if: cap每个真实账户 ORB 到 N（默认 5000），看总量影响。
 * 仅为模拟，不修改任何余额。 GET /api/admin/dashboard/orb-cap-sim
 */
import { ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, InputNumber, Row, Space, Statistic, Table, Tag, Typography } from 'antd';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';

interface OrbCapRow {
  rank: number;
  id: number;
  account: string;
  player_number: string;
  is_sub: boolean;
  orb_before: string;
  orb_after: string;
  reduction: string;
}

interface OrbCapResponse {
  cap: string;
  total_accounts: number;
  affected_count: number;
  sum_before: string;
  sum_after: string;
  sum_reduction: string;
  rows: OrbCapRow[];
}

function trimDecimalZeros(s: string): string {
  return s.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

function fmtWallet(v: string | undefined): string {
  if (!v || v === '0') return '0';
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e8) return `${sign}${trimDecimalZeros((abs / 1e8).toFixed(2))}亿`;
  if (abs >= 1e7) return `${sign}${trimDecimalZeros((abs / 1e7).toFixed(2))}千万`;
  if (abs >= 1e6) return `${sign}${trimDecimalZeros((abs / 1e6).toFixed(2))}百万`;
  if (abs >= 1e4) return `${sign}${trimDecimalZeros((abs / 1e4).toFixed(2))}万`;
  return n.toLocaleString('zh-CN', { maximumFractionDigits: 0 });
}

function fmtNum(v: string): string {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('zh-CN', { maximumFractionDigits: 0 }) : v;
}

function phpUserLink(accountOrId: string | number): string {
  return `/parity/user_user?q=${encodeURIComponent(String(accountOrId))}`;
}

export function OrbCapSimPage() {
  const [capInput, setCapInput] = useState<number>(5000);
  const [cap, setCap] = useState<number>(5000);

  const { data, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'dashboard', 'orb-cap-sim', cap],
    queryFn: () => api.get<OrbCapResponse>('/api/admin/dashboard/orb-cap-sim', { cap }),
    staleTime: 60_000,
  });

  const cardLoading = isLoading || (isFetching && !data);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>ORB 封顶模拟</h2>
        <Space>
          <span style={{ fontSize: 13 }}>封顶值</span>
          <InputNumber
            min={0}
            step={1000}
            value={capInput}
            onChange={(v) => setCapInput(Number(v ?? 0))}
            onPressEnter={() => setCap(Math.max(0, Math.floor(capInput)))}
            style={{ width: 140 }}
          />
          <Button type="primary" onClick={() => setCap(Math.max(0, Math.floor(capInput)))}>
            应用
          </Button>
          {dataUpdatedAt > 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              更新于 {new Date(dataUpdatedAt).toLocaleTimeString()}
            </Typography.Text>
          )}
          <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => void refetch()}>
            刷新
          </Button>
        </Space>
      </div>

      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="加载失败"
          description={error instanceof Error ? error.message : String(error)}
        />
      )}

      <Typography.Paragraph type="secondary" style={{ marginTop: 0, marginBottom: 16, fontSize: 12 }}>
        模拟：把每个真实、<strong>未冻结</strong>账户的 ORB = floor(raw_uo − AP池) 封顶到 {fmtNum(String(cap))}（超过部分削平），统计总量影响。仅为测算，<strong>不修改任何余额</strong>；不含虚拟用户、不含冻结账户。
      </Typography.Paragraph>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" loading={cardLoading}>
            <Statistic
              title={`超过 ${fmtNum(String(cap))} 的账户数`}
              value={data?.affected_count ?? '—'}
              suffix={data ? `/ ${data.total_accounts.toLocaleString('zh-CN')}` : undefined}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" loading={cardLoading}>
            <Statistic title="封顶前 ORB 总额" value={fmtWallet(data?.sum_before)} valueStyle={{ color: '#8c8c8c' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" loading={cardLoading}>
            <Statistic title="封顶后 ORB 总额" value={fmtWallet(data?.sum_after)} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" loading={cardLoading}>
            <Statistic title="削减总额" value={fmtWallet(data?.sum_reduction)} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
      </Row>

      <Typography.Title level={5} style={{ marginTop: 8 }}>
        超过封顶的账户 · 削减量大→小（共 {data?.affected_count?.toLocaleString('zh-CN') ?? '—'} 个）
      </Typography.Title>
      <Card size="small" loading={cardLoading} styles={{ body: { padding: 0 } }}>
        <Table<OrbCapRow>
          size="small"
          bordered
          rowKey="id"
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'], showTotal: (t) => `共 ${t} 条` }}
          scroll={{ x: 760, y: 520 }}
          dataSource={data?.rows ?? []}
          locale={{ emptyText: '没有账户超过该封顶值' }}
          columns={[
            { title: '排名', dataIndex: 'rank', width: 64, fixed: 'left', align: 'center' },
            { title: '玩家编号', dataIndex: 'player_number', width: 100, render: (v: string) => v || '—' },
            {
              title: '账号',
              dataIndex: 'account',
              width: 140,
              ellipsis: true,
              render: (v: string, row) => <Link to={phpUserLink(v || row.id)}>{v}</Link>,
            },
            {
              title: '类型',
              dataIndex: 'is_sub',
              width: 72,
              align: 'center',
              render: (v: boolean) => <Tag color={v ? 'blue' : 'gold'}>{v ? '子账户' : '主账户'}</Tag>,
            },
            { title: '封顶前 ORB', dataIndex: 'orb_before', width: 120, align: 'right', render: (v: string) => <strong>{fmtNum(v)}</strong> },
            { title: '封顶后 ORB', dataIndex: 'orb_after', width: 110, align: 'right', render: (v: string) => fmtNum(v) },
            { title: '削减', dataIndex: 'reduction', width: 110, align: 'right', render: (v: string) => <span style={{ color: '#cf1322' }}>-{fmtNum(v)}</span> },
          ]}
        />
      </Card>
    </div>
  );
}

export default OrbCapSimPage;
